/**
 * PDFViewerPanelPDFView Component - PDF viewer with metadata header
 *
 * Extracted from PDFViewerPanel.tsx
 */

import React from 'react';
import {
  MessageSquare,
  Sparkles,
  FileText,
  StickyNote,
  Tag,
  Info,
  Copy,
  AlertTriangle,
} from 'lucide-react';
import PDFViewerModal from '../PDFViewerModal';
import UsageStats from '../UsageStats';
import type { Theme } from '../../themes/theme';
import { PDFViewerPanelMessageRenderer } from './PDFViewerPanelMessageRenderer';
import { PDFViewerPanelHeader } from './PDFViewerPanelHeader';
import type {
  PDFViewerPanelCitationToView,
  CitationData,
  UsageStatsData,
  RagStatsData,
} from './types/pdf-viewer-panel.types';

interface PDFViewerPanelPDFViewProps {
  citationToView: PDFViewerPanelCitationToView;
  hideHeader: boolean;
  resolvedDocumentId?: string;
  resolvedProjectId?: string;
  currentPage: number | null;
  pdfError: string | null;
  setPdfError: (error: string | null) => void;
  diagnosis: any;
  showDiagnosis: boolean;
  setShowDiagnosis: (show: boolean) => void;
  handlePageChange: (page: number) => void;
  detailTitle?: string;
  detailText?: string;
  notes?: string;
  tags?: string[];
  detailType?: string;
  sourcesCount?: number;
  createdAt?: Date;
  updatedAt?: Date;
  isSavedMessage: boolean;
  isStudioResult: boolean;
  savedMessageCitations: Record<string, CitationData>;
  usageStats?: UsageStatsData | null;
  ragStats?: RagStatsData | null;
  isAdmin: boolean;
  onClose?: () => void;
  onSnippetSaved?: (snippetId: string) => void;
  onCitationClick?: (citation: CitationData) => void;
  onChunkClick?: (citation: CitationData) => void;
  handleCopy: () => void;
  theme: Theme;
}

export const PDFViewerPanelPDFView: React.FC<PDFViewerPanelPDFViewProps> = ({
  citationToView,
  hideHeader,
  resolvedDocumentId,
  resolvedProjectId,
  currentPage,
  pdfError,
  setPdfError,
  diagnosis,
  showDiagnosis,
  setShowDiagnosis,
  handlePageChange,
  detailTitle,
  detailText,
  notes,
  tags,
  detailType,
  sourcesCount,
  createdAt,
  updatedAt,
  isSavedMessage,
  isStudioResult,
  savedMessageCitations,
  usageStats,
  ragStats,
  isAdmin,
  onClose,
  onSnippetSaved,
  onCitationClick,
  onChunkClick,
  handleCopy,
  theme,
}) => {
  return (
    <div className="h-full w-full flex flex-col">
      {!hideHeader && <PDFViewerPanelHeader onClose={onClose} theme={theme} />}

      {(detailTitle ||
        detailText ||
        notes ||
        (tags && tags.length) ||
        detailType ||
        sourcesCount) && (
        <div
          className={`border-b ${theme.colors.border.primary} ${theme.colors.bg.secondary} px-6 py-4 space-y-3`}
        >
          <div className="flex items-start gap-3">
            {isSavedMessage ? (
              <MessageSquare className={`w-5 h-5 mt-1 ${theme.colors.text.secondary}`} />
            ) : isStudioResult ? (
              <Sparkles className={`w-5 h-5 mt-1 ${theme.colors.text.secondary}`} />
            ) : (
              <FileText className={`w-5 h-5 mt-1 ${theme.colors.text.secondary}`} />
            )}
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center justify-between gap-2">
                {detailTitle ? (
                  <h3 className={`text-lg font-semibold ${theme.colors.text.primary}`}>
                    {detailTitle}
                  </h3>
                ) : (
                  <h3 className={`text-lg font-semibold ${theme.colors.text.primary}`}>
                    {isSavedMessage ? 'Mensaje guardado' : 'Fragmento PDF'}
                  </h3>
                )}
                {detailText && (
                  <button
                    onClick={handleCopy}
                    className={`px-3 py-1.5 rounded-lg border ${theme.colors.border.primary} ${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary} transition-colors text-xs font-medium flex items-center gap-1`}
                    title="Copiar contenido"
                  >
                    <Copy className="w-3 h-3" />
                    Copiar
                  </button>
                )}
              </div>
              {detailText && (
                <div
                  className={`${theme.colors.bg.primary} border ${theme.colors.border.primary} rounded-lg p-3`}
                >
                  <div
                    className={`text-sm ${theme.colors.text.primary} whitespace-pre-wrap leading-relaxed`}
                  >
                    <PDFViewerPanelMessageRenderer
                      text={detailText}
                      citations={savedMessageCitations}
                      onCitationClick={onCitationClick}
                      onChunkClick={onChunkClick}
                    />
                  </div>
                </div>
              )}
              {usageStats && (
                <UsageStats stats={usageStats} ragStats={ragStats || undefined} isAdmin={isAdmin} />
              )}
              {(notes ||
                (tags && tags.length) ||
                detailType ||
                sourcesCount ||
                createdAt ||
                updatedAt) && (
                <div className="space-y-3">
                  {notes && (
                    <div>
                      <label
                        className={`text-xs tracking-wide font-semibold flex items-center gap-2 ${theme.colors.text.secondary}`}
                      >
                        <StickyNote className="w-4 h-4" />
                        Notas
                      </label>
                      <div
                        className={`${theme.colors.bg.primary} border ${theme.colors.border.primary} rounded-lg p-3`}
                      >
                        <p
                          className={`text-sm ${theme.colors.text.primary} whitespace-pre-wrap leading-relaxed`}
                        >
                          {notes}
                        </p>
                      </div>
                    </div>
                  )}
                  {tags && tags.length > 0 && (
                    <div>
                      <label
                        className={`text-xs tracking-wide font-semibold flex items-center gap-2 ${theme.colors.text.secondary}`}
                      >
                        <Tag className="w-4 h-4" />
                        Etiquetas
                      </label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {tags.map((tag) => (
                          <span
                            key={tag}
                            className={`px-2 py-1 rounded-full text-xs font-medium ${theme.colors.bg.tertiary} ${theme.colors.text.inverse}`}
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(detailType || sourcesCount !== undefined || createdAt || updatedAt) && (
                    <div className="text-xs space-y-1">
                      <div className={`flex items-center gap-2 ${theme.colors.text.secondary}`}>
                        <Info className="w-4 h-4" />
                        <span className="font-semibold">Detalles</span>
                      </div>
                      <div className={`pl-6 space-y-1 ${theme.colors.text.secondary}`}>
                        {detailType && <p>Tipo: {detailType}</p>}
                        {sourcesCount !== undefined && <p>Fuentes vinculadas: {sourcesCount}</p>}
                        {createdAt && <p>Creado: {createdAt.toLocaleString('es-ES')}</p>}
                        {updatedAt && <p>Actualizado: {updatedAt.toLocaleString('es-ES')}</p>}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error banner */}
      {pdfError && (
        <div
          className={`${theme.colors.action.danger}/10 border-b ${theme.colors.border.focus}/30 px-4 py-3`}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className={`${theme.colors.action.danger} flex-shrink-0`} />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${theme.colors.action.danger} mb-1`}>
                Error al cargar PDF
              </p>
              <p className={`text-xs ${theme.colors.action.danger}`}>{pdfError}</p>
              {diagnosis && (
                <button
                  onClick={() => setShowDiagnosis(!showDiagnosis)}
                  className={`text-xs ${theme.colors.action.danger} hover:text-nb-error/80 underline mt-2`}
                >
                  Ver diagnóstico del sistema
                </button>
              )}
            </div>
            <button
              onClick={() => setPdfError(null)}
              className={`${theme.colors.action.danger} hover:text-nb-error/80 flex-shrink-0`}
            >
              ✕
            </button>
          </div>

          {showDiagnosis && diagnosis && (
            <div
              className={`mt-3 pt-3 border-t ${theme.colors.border.focus}/30 text-xs ${theme.colors.action.danger} space-y-1`}
            >
              <p>• Documentos: {diagnosis.documents?.total || 0}</p>
              <p>• Archivos físicos: {diagnosis.file_system?.files_count || 0}</p>
              {diagnosis.errors?.map((error: string, idx: number) => <p key={idx}>• {error}</p>)}
            </div>
          )}
        </div>
      )}

      {/* PDF Viewer */}
      <div className="flex-1 overflow-hidden">
        <PDFViewerModal
          isOpen={true}
          onClose={() => {}} // No se usa en modo embedded
          documentPath={citationToView.documentPath}
          documentName={citationToView.documentDisplayName || citationToView.documentName}
          documentId={resolvedDocumentId}
          projectId={resolvedProjectId}
          page={currentPage ?? citationToView.page}
          highlightText={citationToView.highlightText}
          snippetCoordinates={citationToView.snippet_coordinates}
          embedded={true} // Modo embedded (sin modal backdrop)
          onError={(error) => setPdfError(error)}
          onPageChange={handlePageChange}
          onClosePanel={onClose}
          onSnippetSaved={onSnippetSaved}
        />
      </div>
    </div>
  );
};
